/**
 * The resume signal (#499).
 *
 * A live view gets its data in two parts: one load when it opens, then the
 * socket events. A gap in the socket loses events, and nothing sends them
 * again. iOS stops an installed app when the person leaves it. The server
 * gets no answer to its pings and closes the socket after 45 s or less, so
 * the events of that time go to rooms without this client. When the person
 * opens the app again, iOS shows the same page with the same data.
 *
 * So each live view reloads its data through its own load function when:
 *   - the socket connects again after a drop (`hooks/useSocket.ts`), or
 *   - the page becomes visible after RESUME_AFTER_MS or more hidden, or
 *   - the browser shows the page again from its back-forward cache.
 *
 * The first signal runs at once. A signal in the next COALESCE_MS runs one
 * more time at the end of that window: a resume often sends two signals (the
 * page becomes visible, then the socket connects again), and the second
 * reload must come after the room joins of the reconnect.
 */
import { useEffect, useRef } from 'react';
import { record, logBackgroundError, markResume } from '../observability';

/** A shorter hide usually keeps the socket, and the socket then delivers the
 *  events. If it drops, the reconnect sends the signal. */
export const RESUME_AFTER_MS = 15_000;
const COALESCE_MS = 2_000;

export type ResumeReason = 'socket' | 'visible' | 'pageshow';

type Listener = () => void;
const listeners = new Set<Listener>();
let lastRun = -Infinity;
let trailing: ReturnType<typeof setTimeout> | null = null;
let trailingReason: ResumeReason | null = null;

// The 2 s window uses the monotonic clock: a step of the wall clock must not
// hold a signal back. The hide check below uses Date.now(), because the
// monotonic clock can stop while the device sleeps.
function run(reason: ResumeReason) {
  lastRun = performance.now();
  markResume();
  // `listeners` counts the subscribers. Three are on every page for a signed-in
  // user (the socket rejoin, the push state and the bell), so a live view makes
  // it 4 or more.
  record({ event: 'app_resume', props: { reason, listeners: String(listeners.size) } });
  // One failed listener must not stop the others.
  listeners.forEach(fn => { try { fn(); } catch (err) { logBackgroundError('resume')(err); } });
}

export function signalResume(reason: ResumeReason): void {
  const since = performance.now() - lastRun;
  if (since >= COALESCE_MS && !trailing) { run(reason); return; }
  trailingReason = reason;
  if (trailing) return;
  trailing = setTimeout(() => {
    trailing = null;
    const r = trailingReason ?? reason;
    trailingReason = null;
    run(r);
  }, Math.max(0, COALESCE_MS - since));
}

/** Calls `fn` on each resume. Returns the unsubscribe. */
export function onResume(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Reloads a live view on each resume. The newest `fn` runs, so the caller
 * does not need a stable reference. A failed reload keeps the data on screen;
 * the error goes to telemetry.
 */
export function useOnResume(fn: () => unknown, context: string): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => onResume(() => {
    Promise.resolve().then(() => ref.current()).catch(logBackgroundError(`resume:${context}`));
  }), [context]);
}

let hiddenAt: number | null = null;
let watching = false;

/** Installs the page watch once (`main.tsx`). */
export function watchForResume(): void {
  if (watching || typeof document === 'undefined') return;
  watching = true;
  if (document.visibilityState === 'hidden') hiddenAt = Date.now();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    const hiddenFor = hiddenAt === null ? 0 : Date.now() - hiddenAt;
    hiddenAt = null;
    if (hiddenFor >= RESUME_AFTER_MS) signalResume('visible');
  });
  // A page from the back-forward cache keeps the state of the moment it left.
  window.addEventListener('pageshow', e => { if (e.persisted) signalResume('pageshow'); });
}
